// Zebra Expr — lexer. Source text -> flat token list.
//
// Tokens: NUMBER, STRING, IDENT, OP (operators/punctuation), MOD (#name),
// EOF. Keywords (IF/ELSE/WITH/AND/OR/NOT/true/false/null) are lexed as IDENT
// and recognised by the parser. Comments and whitespace are skipped.

export class ExprError extends Error {
  constructor(message, pos) {
    super(message);
    this.name = 'ExprError';
    this.pos = pos;
  }
}

// Two-char operators first so we don't split "!=" into "!" "=".
// `->` is the arrow of an explicit lambda (`x -> x * 2`, `(a, b) -> a + b`).
const TWO = ['!=', '<=', '>=', '->'];
// `;` is accepted as an argument separator too — Structure Expr uses it (and
// mixes it with `,`) in function calls like IF(c; r; ...) and CONCAT(a; b).
const ONE = ['=', '<', '>', '+', '-', '*', '/', '%', '(', ')', '{', '}', ',', ';', ':', '.'];

export function tokenize(src) {
  const s = String(src || '');
  const n = s.length;
  const toks = [];
  let i = 0;

  const isIdentStart = (c) => /[A-Za-z_]/.test(c);
  const isIdentPart = (c) => /[A-Za-z0-9_]/.test(c);
  const isDigit = (c) => c >= '0' && c <= '9';

  while (i < n) {
    const c = s[i];

    // whitespace
    if (/\s/.test(c)) { i++; continue; }

    // comments
    if (c === '/' && s[i + 1] === '/') { while (i < n && s[i] !== '\n') i++; continue; }
    if (c === '/' && s[i + 1] === '*') {
      i += 2;
      while (i < n && !(s[i] === '*' && s[i + 1] === '/')) i++;
      i += 2;
      continue;
    }

    // triple-quoted template: """ text ${expr} more """ — parts are captured
    // raw; the parser sub-parses each ${...} expression. Structure uses these
    // for formatted/interpolated output.
    if (s.startsWith('"""', i)) {
      const start = i;
      i += 3;
      const parts = [];
      let lit = '';
      while (i < n && !s.startsWith('"""', i)) {
        if (s[i] === '$' && s[i + 1] === '{') {
          if (lit) { parts.push({ t: 'str', v: lit }); lit = ''; }
          i += 2;
          let depth = 1;
          let expr = '';
          while (i < n && depth > 0) {
            const ch = s[i];
            if (ch === '{') depth++;
            else if (ch === '}') { depth--; if (depth === 0) { i++; break; } }
            // skip over quoted strings inside the interpolation
            if (depth > 0 && (ch === '"' || ch === "'")) {
              const q = ch; expr += ch; i++;
              while (i < n && s[i] !== q) { if (s[i] === '\\') { expr += s[i] + (s[i + 1] || ''); i += 2; } else expr += s[i++]; }
              if (i < n) { expr += s[i]; i++; }
              continue;
            }
            expr += ch; i++;
          }
          parts.push({ t: 'expr', src: expr });
        } else {
          lit += s[i++];
        }
      }
      if (i >= n) throw new ExprError('Unterminated triple-quoted string', start);
      if (lit) parts.push({ t: 'str', v: lit });
      i += 3; // closing """
      toks.push({ type: 'TEMPLATE', parts, pos: start });
      continue;
    }

    // string literal (single or double quoted, backslash escapes)
    if (c === '"' || c === "'") {
      const quote = c;
      const start = i;
      i++;
      let out = '';
      while (i < n && s[i] !== quote) {
        if (s[i] === '\\' && i + 1 < n) {
          const e = s[i + 1];
          out += e === 'n' ? '\n' : e === 't' ? '\t' : e;
          i += 2;
        } else {
          out += s[i++];
        }
      }
      if (i >= n) throw new ExprError('Unterminated string', start);
      i++; // closing quote
      toks.push({ type: 'STRING', value: out, pos: start });
      continue;
    }

    // number (123, 1.5, .5)
    if (isDigit(c) || (c === '.' && isDigit(s[i + 1]))) {
      const start = i;
      while (i < n && (isDigit(s[i]) || s[i] === '.')) i++;
      toks.push({ type: 'NUMBER', value: parseFloat(s.slice(start, i)), pos: start });
      continue;
    }

    // modifier (#name) — used after aggregate names, e.g. SUM#children
    if (c === '#') {
      const start = i;
      i++;
      let name = '';
      while (i < n && isIdentPart(s[i])) name += s[i++];
      if (!name) throw new ExprError('Expected modifier name after "#"', start);
      // optional =value (number or bareword or quoted) — captured raw for later
      let modValue;
      if (s[i] === '=') {
        i++;
        if (s[i] === '"' || s[i] === "'") {
          const q = s[i++]; let v = '';
          while (i < n && s[i] !== q) v += s[i++];
          i++;
          modValue = v;
        } else {
          let v = '';
          while (i < n && !/[\s{},:)]/.test(s[i])) v += s[i++];
          modValue = v;
        }
      }
      toks.push({ type: 'MOD', value: name, modValue, pos: start });
      continue;
    }

    // `$`, `$1`, `$2` … — the implicit-function placeholders Structure uses in
    // array calls (`worklogs.FILTER($.author = ME())`): `$` is the element
    // being processed and `$N` the N-th argument of a multi-parameter function
    // (REDUCE's accumulator and element). Lexed as identifiers so the parser
    // and interpreter treat them as ordinary names bound by the call.
    if (c === '$') {
      const start = i;
      i++;
      while (i < n && isDigit(s[i])) i++;
      toks.push({ type: 'IDENT', value: s.slice(start, i), pos: start });
      continue;
    }

    // identifier / keyword
    if (isIdentStart(c)) {
      const start = i;
      while (i < n && isIdentPart(s[i])) i++;
      toks.push({ type: 'IDENT', value: s.slice(start, i), pos: start });
      continue;
    }

    // operators / punctuation
    const two = s.slice(i, i + 2);
    // `&&` / `||` are accepted as aliases for the `and` / `or` keywords (many
    // pasted Jira/Structure formulas use them) — emit the exact IDENT token the
    // keywords produce so the parser treats them identically.
    if (two === '&&') { toks.push({ type: 'IDENT', value: 'and', pos: i }); i += 2; continue; }
    if (two === '||') { toks.push({ type: 'IDENT', value: 'or', pos: i }); i += 2; continue; }
    if (TWO.includes(two)) { toks.push({ type: 'OP', value: two, pos: i }); i += 2; continue; }
    if (ONE.includes(c)) { toks.push({ type: 'OP', value: c, pos: i }); i++; continue; }

    throw new ExprError(`Unexpected character "${c}"`, i);
  }

  toks.push({ type: 'EOF', value: null, pos: n });
  return toks;
}
